import { AppMainContextType, AppSubContextType } from "../types/app-context"
import { isMainContext, isOrgContextType, isSubContext } from "./context-option"
import { isCommonRoute, getValidRouteSegmentsByContext } from "./route-config"

export const getDefaultRouteForContext = (context: AppMainContextType | AppSubContextType): string => {
  return isOrgContextType(context.type) ? "projects" : "home"
}

const addContextToSearchParams = (searchParams: URLSearchParams, context: AppMainContextType | AppSubContextType) => {
  const mainContextId = isMainContext(context) ? context.id : context.parentContextId
  const subContextId = isSubContext(context) ? context.id : undefined

  searchParams.set("mainContext", mainContextId)
  if (subContextId) {
    searchParams.set("subContext", subContextId)
  } else {
    searchParams.delete("subContext")
  }
}

export const buildDefaultPathWithContext = ({
  currentSearch,
  context,
}: {
  currentSearch?: string
  context: AppMainContextType | AppSubContextType
}): string => {
  const route = getDefaultRouteForContext(context)
  const path = isCommonRoute(route) ? `/${route}` : `${context.homePage}/${route}`
  const searchParams = new URLSearchParams(currentSearch)
  addContextToSearchParams(searchParams, context)
  const search = searchParams.toString()

  return search ? `${path}?${search}` : path
}

const findMatchingSegment = (currentSegment: string, nextValidSegments: string[]) => {
  let matchedSegment = null
  const validSegmentsClone = [...nextValidSegments]

  while (!matchedSegment && validSegmentsClone.length > 0) {
    const segment = validSegmentsClone.shift()
    matchedSegment = currentSegment === segment ? segment : null
  }

  return matchedSegment
}

function createNextSegment({
  currentPath,
  nextContext,
  currentContext = nextContext,
}: {
  currentPath: string
  currentContext?: AppMainContextType | AppSubContextType
  nextContext: AppMainContextType | AppSubContextType
}): string {
  const validSegmentsForNextContext = getValidRouteSegmentsByContext(nextContext.type)
  const cleanedUpSegment = getCleanedUpSegment(currentPath, currentContext)
  const matchedSegment = findMatchingSegment(cleanedUpSegment, validSegmentsForNextContext)
  const fallbackSegment = getDefaultRouteForContext(nextContext)
  const nextPathSegment = matchedSegment || fallbackSegment
  const segment = isCommonRoute(nextPathSegment) ? `/${nextPathSegment}` : `${nextContext.homePage}/${nextPathSegment}`
  return segment
}

function getCleanedUpSegment(path: string, context: AppMainContextType | AppSubContextType): string {
  const pathWithoutQueryOrHash = path.split("?")[0].split("#")[0]

  const cleanedUpSegment = pathWithoutQueryOrHash
    .replace(`${context.homePage}/`, "") // Remove the home page prefix if it exists (e.g. "/memberships/settings" becomes "settings")
    .replace(/^\/|\/$/g, "") // Remove leading and trailing slashes for common routes

  return cleanedUpSegment
}

export function buildPathWithContext({
  currentPath,
  currentSearch,
  nextContext,
  currentContext,
}: {
  currentPath: string
  currentSearch?: string
  nextContext: AppMainContextType | AppSubContextType
  currentContext?: AppMainContextType | AppSubContextType
}): string {
  const nextSegment = createNextSegment({ currentPath, nextContext, currentContext })
  const searchParams = new URLSearchParams(currentSearch)
  addContextToSearchParams(searchParams, nextContext)

  const search = searchParams.toString()
  return search ? `${nextSegment}?${search}` : nextSegment
}
